/**
 * CRT Program Settings -- platform admin custodian wire instruction CRUD.
 *
 * Sprint C-CRT-01 + 2026-05-16 role-equivalence fix.
 *
 * Custodian FI wire instructions are owned by Aaim Platform Operators
 * (platform-admin scope). The list view masks account numbers (last 4
 * digits) since the backend persists AES-256-GCM ciphertext; the full
 * plaintext is collected through the edit form and never read back. The
 * upsert is keyed by custodianFiId, so saving the same record updates
 * the existing row.
 *
 * Authz: gated via case-insensitive ANY-of role check accepting
 * `aaim_super_admin`, `platform_admin`, or `e2e_test_admin` (the three
 * role names that share the platform-admin permission scope by design
 * per aaim-core seed-rbac.ts; see fixture memory). FI tenant operators
 * cannot access this page. Compare logic per
 * .claude/rules/authz-case-insensitive.md.
 */

import { useState } from 'react';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import {
  Card,
  Heading,
  Button,
  Badge,
  Input,
  Select,
  FormLabel,
  FormHelperText,
  Skeleton,
  Dialog,
  TableScroll,
} from '@aaimplatform/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components as CrtComponents } from '@aaimplatform/institutional-types/generated/cooperative-reserve-token';
import { cooperativeReserveTokenClient } from '../../api/typed-clients';
import { useAuthStore } from '../../stores/auth';
import { hasAnyRole, PLATFORM_ADMIN_EQUIVALENT_ROLES } from '../../utils/role-helpers';

type CustodianConfigDto = CrtComponents['schemas']['CustodianConfigDto'];
type CustodianConfigInput = CrtComponents['schemas']['CustodianConfigInput'];

interface FormState {
  custodianFiId: string;
  accountName: string;
  accountNumber: string;
  routingNumber: string;
  swiftCode: string;
  bankName: string;
  fedwireObi: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  custodianFiId: '',
  accountName: '',
  accountNumber: '',
  routingNumber: '',
  swiftCode: '',
  bankName: '',
  fedwireObi: '',
  active: true,
};

function formToInput(form: FormState): CustodianConfigInput {
  return {
    custodianFiId: form.custodianFiId.trim(),
    accountName: form.accountName.trim(),
    accountNumber: form.accountNumber.trim(),
    routingNumber: form.routingNumber.trim(),
    swiftCode: form.swiftCode.trim() || undefined,
    bankName: form.bankName.trim(),
    fedwireObi: form.fedwireObi.trim() || undefined,
    active: form.active,
  };
}

function validateForm(form: FormState): string | null {
  if (!form.custodianFiId.trim()) return 'Custodian FI identifier is required';
  if (!form.accountName.trim()) return 'Account name is required';
  if (!form.accountNumber.trim()) return 'Account number is required';
  if (!form.routingNumber.trim()) return 'Routing number is required';
  if (form.routingNumber.replace(/\D/g, '').length !== 9) {
    return 'Routing number must be 9 digits';
  }
  if (!form.bankName.trim()) return 'Bank name is required';
  return null;
}

export function CrtProgramSettings() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);

  const isAuthorized = hasAnyRole(user?.roles, PLATFORM_ADMIN_EQUIVALENT_ROLES);

  const { data, isLoading, error } = useQuery({
    queryKey: ['crt-custodian-configs'],
    queryFn: async () => {
      const { data, error } = await cooperativeReserveTokenClient.GET(
        '/api/v1/crt/custodian-config',
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ??
            'Failed to load custodian configurations',
        );
      }
      return (data?.configs ?? []) as CustodianConfigDto[];
    },
    enabled: isAuthorized,
    staleTime: 30 * 1000,
    retry: false,
  });

  const upsertMutation = useMutation({
    mutationFn: async (input: CustodianConfigInput) => {
      const { data, error } = await cooperativeReserveTokenClient.PUT(
        '/api/v1/crt/custodian-config',
        { body: input },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Save failed',
        );
      }
      return data as CustodianConfigDto;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crt-custodian-configs'] });
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
    },
  });

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDialogOpen(true);
    upsertMutation.reset();
  }

  function openEdit(config: CustodianConfigDto) {
    setForm({
      custodianFiId: config.custodianFiId,
      accountName: config.accountName,
      accountNumber: '',
      routingNumber: config.routingNumber,
      swiftCode: config.swiftCode ?? '',
      bankName: config.bankName,
      fedwireObi: config.fedwireObi ?? '',
      active: config.active,
    });
    setEditingId(config.configId);
    setDialogOpen(true);
    upsertMutation.reset();
  }

  function handleSubmit() {
    const validationError = validateForm(form);
    if (validationError) {
      upsertMutation.reset();
      // Surface validation by setting an error directly through the form.
      // The mutation will not be invoked.
      return;
    }
    upsertMutation.mutate(formToInput(form));
  }

  const formValidationError = validateForm(form);

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldAlert className="w-12 h-12 text-warning mb-3" />
        <Heading level={2} size="card" className="mb-2">
          Restricted access
        </Heading>
        <p className="text-sm font-sans text-tertiary">
          CRT program settings require platform-admin scope.
        </p>
      </div>
    );
  }

  const configs = data ?? [];

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <p className="text-sm font-sans text-tertiary">
            Custodian FI wire instructions and program configuration.
            Account numbers are encrypted at rest and never re-displayed.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          Add custodian configuration
        </Button>
      </div>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {error instanceof Error ? error.message : 'Failed to load'}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : configs.length === 0 ? (
        <Card className="p-6 bg-canvas/50">
          <div className="text-sm font-sans text-tertiary">
            No custodian configurations recorded. Add one to enable
            subscriptions for FI tenants.
          </div>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <TableScroll label="Custodian configurations">
          <table className="w-full min-w-table-wide text-sm font-sans">
            <thead className="bg-canvas/50 border-b border-recessed">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Custodian FI ID
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Bank
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Account
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Routing
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Account #
                </th>
                <th className="text-left px-4 py-2 text-xs text-tertiary font-medium">
                  Status
                </th>
                <th className="text-right px-4 py-2 text-xs text-tertiary font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => (
                <tr
                  key={config.configId}
                  className="border-b border-recessed last:border-b-0"
                >
                  <td className="px-4 py-3 font-mono tabular-nums text-primary">
                    {config.custodianFiId.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-3 text-primary">{config.bankName}</td>
                  <td className="px-4 py-3 text-primary">{config.accountName}</td>
                  <td className="px-4 py-3 font-mono tabular-nums text-primary">
                    {config.routingNumber}
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums text-primary">
                    {config.accountNumberMasked}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={config.active ? 'success' : 'neutral'}
                      size="sm"
                    >
                      {config.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => openEdit(config)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </Card>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDialogOpen(false);
            setEditingId(null);
            setForm(EMPTY_FORM);
            upsertMutation.reset();
          }
        }}
        title={
          editingId
            ? 'Edit custodian configuration'
            : 'Add custodian configuration'
        }
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setDialogOpen(false);
                setEditingId(null);
                setForm(EMPTY_FORM);
                upsertMutation.reset();
              }}
              disabled={upsertMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                upsertMutation.isPending || formValidationError !== null
              }
            >
              {upsertMutation.isPending ? 'Saving...' : 'Save configuration'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormLabel htmlFor="config-custodian-fi-id" required>
              Custodian FI identifier
            </FormLabel>
            <Input
              id="config-custodian-fi-id"
              type="text"
              value={form.custodianFiId}
              onChange={(e) =>
                setForm((s) => ({ ...s, custodianFiId: e.target.value }))
              }
              disabled={editingId !== null}
              placeholder="UUID of the participating FI playing custodian role"
            />
            <FormHelperText>
              The CrtParticipant whose status is Custodian. Cannot be changed
              on an existing configuration.
            </FormHelperText>
          </div>
          <div>
            <FormLabel htmlFor="config-bank-name" required>
              Bank name
            </FormLabel>
            <Input
              id="config-bank-name"
              type="text"
              value={form.bankName}
              onChange={(e) =>
                setForm((s) => ({ ...s, bankName: e.target.value }))
              }
              placeholder="Beneficiary bank legal name"
            />
          </div>
          <div>
            <FormLabel htmlFor="config-account-name" required>
              Account name
            </FormLabel>
            <Input
              id="config-account-name"
              type="text"
              value={form.accountName}
              onChange={(e) =>
                setForm((s) => ({ ...s, accountName: e.target.value }))
              }
              placeholder="Beneficiary on the wire"
            />
          </div>
          <div>
            <FormLabel htmlFor="config-routing" required>
              Routing number
            </FormLabel>
            <Input
              id="config-routing"
              type="text"
              value={form.routingNumber}
              onChange={(e) =>
                setForm((s) => ({ ...s, routingNumber: e.target.value }))
              }
              placeholder="9 digits"
            />
          </div>
          <div>
            <FormLabel htmlFor="config-account" required>
              Account number
            </FormLabel>
            <Input
              id="config-account"
              type="text"
              value={form.accountNumber}
              onChange={(e) =>
                setForm((s) => ({ ...s, accountNumber: e.target.value }))
              }
              placeholder={
                editingId
                  ? 'Enter to replace existing encrypted value'
                  : 'Plaintext, encrypted on save'
              }
            />
            <FormHelperText>
              Persisted as AES-256-GCM ciphertext. Never readable back.
            </FormHelperText>
          </div>
          <div>
            <FormLabel htmlFor="config-swift">SWIFT / BIC (optional)</FormLabel>
            <Input
              id="config-swift"
              type="text"
              value={form.swiftCode}
              onChange={(e) =>
                setForm((s) => ({ ...s, swiftCode: e.target.value }))
              }
              placeholder="For cross-border wires"
            />
          </div>
          <div className="md:col-span-2">
            <FormLabel htmlFor="config-obi">
              Fedwire OBI prefix (optional)
            </FormLabel>
            <Input
              id="config-obi"
              type="text"
              value={form.fedwireObi}
              onChange={(e) =>
                setForm((s) => ({ ...s, fedwireObi: e.target.value }))
              }
              placeholder="Memo line prefix"
            />
            <FormHelperText>
              Appears on outbound wire memos alongside the Funding Reference.
            </FormHelperText>
          </div>
          <div>
            <FormLabel htmlFor="config-active">Status</FormLabel>
            <Select
              id="config-active"
              value={form.active ? 'true' : 'false'}
              onChange={(e) =>
                setForm((s) => ({ ...s, active: e.target.value === 'true' }))
              }
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
            />
          </div>
        </div>

        {formValidationError && (
          <div className="mt-4 bg-warning/10 border border-warning/30 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-warning flex-shrink-0" />
            <div className="text-xs font-sans text-secondary">
              {formValidationError}
            </div>
          </div>
        )}

        {upsertMutation.isError && (
          <div className="mt-4 bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="text-xs font-sans text-error">
              {upsertMutation.error instanceof Error
                ? upsertMutation.error.message
                : 'Save failed'}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
